import { KpiConfigPeriod, KpiSection } from '../../data-models/company-financials.data-model';
import { MaggieHttp } from '../MaggieHttp';

export const COMPANY_VIEWS_COMPONENT = 'portfolioReporting';

/** One synthesized column: a (period, section) combination. */
export interface IKpiDisplaySelection {
  period: KpiConfigPeriod;
  section: KpiSection;
}

/** Components (and column selections) a KPI field is registered to display in. */
export interface IKpiDisplayConfig {
  component: typeof COMPANY_VIEWS_COMPONENT;
  selections: IKpiDisplaySelection[];
}

export async function getKpiDisplayConfig(kpiId: number): Promise<IKpiDisplayConfig[]> {
  const { data } = await MaggieHttp.maggie.get<IKpiDisplayConfig[]>(`/fieldDisplayConfig/kpi/${kpiId}`);
  return data ?? [];
}

/** An empty `selections` array deregisters the KPI from the component. */
export async function updateKpiDisplayConfig(
  kpiId: number,
  payload: IKpiDisplayConfig
): Promise<IKpiDisplayConfig[]> {
  const { data } = await MaggieHttp.maggie.put<IKpiDisplayConfig, IKpiDisplayConfig[]>(
    `/fieldDisplayConfig/kpi/${kpiId}`,
    payload
  );
  return data;
}

export interface IBulkKpiDisplayResult {
  updated: number[];
  skipped: number[];
}

/** Replaces every listed KPI's display config with the given selections. */
export async function bulkUpdateKpiDisplayConfig(
  kpiIds: number[],
  payload: IKpiDisplayConfig
): Promise<IBulkKpiDisplayResult> {
  const { data } = await MaggieHttp.maggie.put<
    IKpiDisplayConfig & { kpiIds: number[] },
    IBulkKpiDisplayResult
  >(`/fieldDisplayConfig/kpi/bulk`, { ...payload, kpiIds });
  return data;
}

/**
 * A formula field maps to exactly one column, so its registration is a single
 * flag rather than a set of (period, section) selections.
 */
export interface IFormulaDisplayConfig {
  component: typeof COMPANY_VIEWS_COMPONENT;
  displayed: boolean;
}

/**
 * Ids of the formula fields registered to display. Formula columns are
 * injected by the frontend, so it reads the whole set once instead of asking
 * per field.
 */
export async function getDisplayedFormulaFieldIds(): Promise<number[]> {
  const { data } = await MaggieHttp.maggie.get<number[]>('/fieldDisplayConfig/formula');
  return data ?? [];
}

export async function updateFormulaDisplayConfig(
  formulaFieldId: number,
  payload: IFormulaDisplayConfig
): Promise<IFormulaDisplayConfig> {
  const { data } = await MaggieHttp.maggie.put<IFormulaDisplayConfig, IFormulaDisplayConfig>(
    `/fieldDisplayConfig/formula/${formulaFieldId}`,
    payload
  );
  return data;
}

/** Registers or deregisters every listed formula field. */
export async function bulkUpdateFormulaDisplayConfig(
  formulaFieldIds: number[],
  payload: IFormulaDisplayConfig
): Promise<IBulkKpiDisplayResult> {
  const { data } = await MaggieHttp.maggie.put<
    IFormulaDisplayConfig & { formulaFieldIds: number[] },
    IBulkKpiDisplayResult
  >(`/fieldDisplayConfig/formula/bulk`, { ...payload, formulaFieldIds });
  return data;
}
